import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import { Award, Crown, Users as UsersIcon, Trophy, Medal, Star, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function shiftDays(iso, d) { const dt = new Date(iso); dt.setDate(dt.getDate() + d); return dt.toISOString().slice(0, 10); }

// Opsi urutkan – key sesuai field di backend leaderboard row
const SORT_OPTIONS = [
  { value: "nominal_total",   label: "Total Nominal (terbesar)" },
  { value: "total",           label: "Jumlah Surat (terbanyak)" },
  { value: "sph",             label: "Jumlah SPH (terbanyak)" },
  { value: "invoice",         label: "Jumlah Invoice (terbanyak)" },
  { value: "nominal_sph",     label: "Nilai SPH (terbesar)" },
  { value: "nominal_invoice", label: "Nilai Invoice (terbesar)" },
  { value: "selesai",         label: "Jumlah Selesai (terbanyak)" },
  { value: "klien_unik",      label: "Jumlah Klien (terbanyak)" },
];

const STATUS_OPTIONS = [
  { value: "all",       label: "Semua status" },
  { value: "Selesai",   label: "Hanya Selesai" },
  { value: "Pengajuan", label: "Hanya Pengajuan" },
  { value: "Batal",     label: "Hanya Batal" },
];

const RANK_STYLE = [
  { bg: "bg-gradient-to-br from-amber-400 to-amber-600", text: "text-white", icon: Crown, label: "Juara 1" },
  { bg: "bg-gradient-to-br from-slate-300 to-slate-500", text: "text-white", icon: Trophy, label: "Juara 2" },
  { bg: "bg-gradient-to-br from-orange-300 to-orange-500", text: "text-white", icon: Medal, label: "Juara 3" },
];

function PodiumCard({ row, rank, sortBy, sortLabel }) {
  const cfg = RANK_STYLE[rank];
  if (!row) return <Card className="border-dashed p-6 text-center text-sm text-ink-500">—</Card>;
  const Icon = cfg.icon;
  const isMoney = sortBy.startsWith("nominal");
  const primaryValue = isMoney ? formatRupiah(row[sortBy]) : String(row[sortBy] ?? 0);
  return (
    <Card className={`relative overflow-hidden border-0 p-5 ${cfg.bg} ${cfg.text} shadow-lg`} data-testid={`podium-rank-${rank+1}`}>
      <div className="absolute right-3 top-3 opacity-30"><Icon className="h-16 w-16" /></div>
      <div className="relative">
        <div className="text-xs font-medium uppercase tracking-widest opacity-90">{cfg.label}</div>
        <div className="mt-1 font-heading text-2xl font-bold leading-tight">{row.label}</div>
        <div className="mt-2 rounded bg-white/15 px-2.5 py-1.5 backdrop-blur">
          <div className="text-[10px] uppercase tracking-wider opacity-80">{sortLabel}</div>
          <div className="font-mono text-base font-bold leading-tight">{primaryValue}</div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded bg-white/15 px-2 py-1 backdrop-blur">
            <div className="opacity-80">Total Surat</div>
            <div className="font-bold">{row.total}</div>
          </div>
          <div className="rounded bg-white/15 px-2 py-1 backdrop-blur">
            <div className="opacity-80">Selesai</div>
            <div className="font-bold">{row.selesai}/{row.total}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className={`rounded-md border border-border bg-card px-2.5 py-1.5 text-center`}>
      <div className="text-[10px] uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`mt-0.5 font-mono text-xs font-bold ${color || "text-ink-900"}`}>{value}</div>
    </div>
  );
}

function LeaderboardTable({ rows, type, sortBy }) {
  if (!rows?.length) return <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-ink-500">Belum ada data pada rentang ini.</div>;
  const cols = [
    { key: "total",           label: "Total Surat",   right: true, fmt: v => v },
    { key: "sph",             label: "SPH",           right: true, fmt: v => v, color: "text-sky-700" },
    { key: "invoice",         label: "Invoice",       right: true, fmt: v => v, color: "text-amber-700" },
    { key: "nominal_sph",     label: "Nilai SPH",     right: true, fmt: formatRupiah, color: "text-sky-700", mono: true },
    { key: "nominal_invoice", label: "Nilai Invoice", right: true, fmt: formatRupiah, color: "text-amber-700", mono: true },
    { key: "nominal_total",   label: "Total Nilai",   right: true, fmt: formatRupiah, mono: true, bold: true },
    { key: "selesai",         label: "Selesai",       right: true, fmt: (v, r) => `${v}/${r.total}` },
    { key: "klien_unik",      label: "Klien",         right: true, fmt: v => v, bold: true },
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={`leaderboard-${type}`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-xs">
          <tr className="text-left">
            <th className="px-3 py-2.5">#</th>
            <th className="px-3 py-2.5">{type === "users" ? "User" : "Marketing"}</th>
            {cols.map(c => (
              <th key={c.key} className={`px-3 py-2.5 ${c.right ? "text-right" : ""} ${sortBy === c.key ? "bg-brand/10 text-brand" : ""}`}>{c.label}{sortBy === c.key ? " ▼" : ""}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => {
            const isTop = i < 3;
            return (
              <tr key={r.key} className={isTop ? "bg-amber-50/40" : ""}>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i===0?"bg-amber-400 text-white":i===1?"bg-slate-400 text-white":i===2?"bg-orange-400 text-white":"bg-secondary text-ink-500"}`}>{i+1}</span>
                </td>
                <td className="px-3 py-2.5 font-medium text-ink-900">{r.label}</td>
                {cols.map(c => (
                  <td key={c.key} className={`px-3 py-2.5 text-right ${c.color || ""} ${c.mono ? "font-mono text-xs" : ""} ${c.bold ? "font-bold text-ink-900" : ""} ${sortBy === c.key ? "bg-brand/5 font-bold" : ""}`}>
                    {c.key === "selesai" ? (
                      <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">{c.fmt(r[c.key], r)}</span>
                    ) : c.fmt(r[c.key], r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportPage() {
  const [start, setStart] = useState(shiftDays(todayIso(), -89));
  const [end, setEnd] = useState(todayIso());
  const [sortBy, setSortBy] = useState("nominal_total");
  const [statusFilter, setStatusFilter] = useState("all");
  const [data, setData] = useState({ users: [], marketing: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/reports/leaderboard", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [start, end, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Apply client-side sorting on the result
  const sortedUsers = useMemo(() => {
    return [...(data.users || [])].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [data, sortBy]);
  const sortedMarketing = useMemo(() => {
    return [...(data.marketing || [])].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [data, sortBy]);

  const sortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label || "";

  // Aggregate stats for top KPIs
  const overall = useMemo(() => {
    const all = data.users || [];
    return all.reduce((acc, r) => ({
      total: acc.total + r.total,
      sph: acc.sph + r.sph,
      invoice: acc.invoice + r.invoice,
      nominal: acc.nominal + r.nominal_total,
      selesai: acc.selesai + r.selesai,
      clients: acc.clients + r.klien_unik,
    }), { total: 0, sph: 0, invoice: 0, nominal: 0, selesai: 0, clients: 0 });
  }, [data]);

  return (
    <div className="space-y-6" data-testid="report-page">
      <PageHeader overline="Performa" title="Report & Leaderboard" description="Siapa user terbaik & marketing terbaik di periode ini, lengkap dengan rincian SPH, Invoice, klien, dan tingkat penyelesaian." icon={Award} tone="brand" />

      {/* Filter */}
      <Card className="p-5 border-border shadow-none">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div><Label>Dari</Label><DatePicker value={start} onChange={setStart} testId="rep-start" placeholder="Tanggal awal" /></div>
          <div><Label>Sampai</Label><DatePicker value={end} onChange={setEnd} testId="rep-end" placeholder="Tanggal akhir" /></div>
          <div>
            <Label className="flex items-center gap-1"><ArrowDownWideNarrow className="h-3 w-3" /> Urutkan berdasarkan</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="rep-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Filter Status Surat</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="rep-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink-500">
          <span className="rounded-full bg-secondary px-2.5 py-1">📊 {sortLabel}</span>
          {statusFilter !== "all" && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">🎯 Status: {statusFilter}</span>}
          {loading && <span className="text-brand">Memuat…</span>}
        </div>
      </Card>

      {/* Overall KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatPill label="Total Surat" value={overall.total} />
        <StatPill label="SPH" value={overall.sph} color="text-sky-700" />
        <StatPill label="Invoice" value={overall.invoice} color="text-amber-700" />
        <StatPill label="Selesai" value={overall.selesai} color="text-emerald-700" />
        <StatPill label="Klien Unik" value={overall.clients} />
        <div className="col-span-2 lg:col-span-1 rounded-md border border-border bg-gradient-to-br from-brand to-brand-700 px-2.5 py-1.5 text-center text-white">
          <div className="text-[10px] uppercase tracking-wider opacity-90">Total Nominal</div>
          <div className="mt-0.5 font-mono text-xs font-bold">{formatRupiah(overall.nominal)}</div>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="mr-2 h-4 w-4" />Top User</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing"><Star className="mr-2 h-4 w-4" />Top Marketing</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {[0,1,2].map(i => <PodiumCard key={i} row={sortedUsers[i]} rank={i} sortBy={sortBy} sortLabel={sortLabel} />)}
          </div>
          <LeaderboardTable rows={sortedUsers} type="users" sortBy={sortBy} />
        </TabsContent>

        <TabsContent value="marketing" className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {[0,1,2].map(i => <PodiumCard key={i} row={sortedMarketing[i]} rank={i} sortBy={sortBy} sortLabel={sortLabel} />)}
          </div>
          <LeaderboardTable rows={sortedMarketing} type="marketing" sortBy={sortBy} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
